import React, { useState, useEffect } from 'react';
import { BarChart3, TrendingUp, TrendingDown, Users, AlertTriangle, CheckCircle, Loader2, FileInput, FileCheck, RefreshCw, ShieldCheck, ShieldX } from 'lucide-react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { getApiUrl, getServerName, getSystemID, authFetch } from '../../config/api';
// Removed PageHeader import


function PatronDashboard() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summaryData, setSummaryData] = useState(null);

  // Fetch dashboard data from API
  useEffect(() => {
    const fetchSummaryData = async () => {
      try {
        setLoading(true);
        const response = await authFetch(getApiUrl('GET_RX_PROFILES_SUMMARY'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ServerName: getServerName(),
            SystemID: getSystemID(),
            Purpose: "GETRXPROFILESUMMARY",
            Source: "RXPROFILEWEB",
            DeviceType: "RXPROFILEWEB"
          })
        });
        
        if (!response.ok) {
          throw new Error(`API request failed with status ${response.status}`);
        }
        
        const data = await response.json();
        
        if (data.ResponseCode !== "0") {
          throw new Error(`API returned error: ${data.ResponseMessage}`);
        }
        
        setSummaryData(data.OutputData);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
        setError(errorMessage);
        setSummaryData(null);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSummaryData();
  }, []); // Only run once on component mount

  // Helper function to safely parse number from string
  const safeParseInt = (value) => {
    if (!value || value === 'NA' || value === '') return 0;
    const parsed = parseInt(value);
    return isNaN(parsed) ? 0 : parsed;
  };

  // Helper function to get dashboard data from API response
  const getDashboardData = () => {
    if (!summaryData) {
      return {
        profileStats: {
          total: 0,
          highConfidence: 0,
          mediumConfidence: 0,
          lowConfidence: 0
        },
        profilesCreated: {
          ytd: { current: 0, previous: 0 },
          mtd: { current: 0, previous: 0 }
        },
        profilesAccessed: {
          ytd: { current: 0, previous: 0 },
          mtd: { current: 0, previous: 0 }
        },
        kycDue: 0,
        verification: {
          dvs: { submitted: 0, verified: 0, retries: 0 },
          aml: { requests: 0, approved: 0, rejected: 0 }
        }
      };
    }

    return {
      profileStats: {
        total: safeParseInt(summaryData.TotalProfilesCount),
        highConfidence: safeParseInt(summaryData.HighConfidenceProfilesCount),
        mediumConfidence: safeParseInt(summaryData.MediumConfidenceProfilesCount),
        lowConfidence: safeParseInt(summaryData.LowConfidenceProfilesCount)
      },
      profilesCreated: {
        ytd: { 
          current: safeParseInt(summaryData.CurrentYTDProfileCreatedCount), 
          previous: safeParseInt(summaryData.PrevYTDProfileCreatedCount) 
        },
        mtd: { 
          current: safeParseInt(summaryData.CurrentMTDProfileCreatedCount), 
          previous: safeParseInt(summaryData.PrevMTDProfileCreatedCount) 
        }
      },
      profilesAccessed: {
        ytd: { 
          current: safeParseInt(summaryData.CurrentYTDProfileAccessedCount), 
          previous: safeParseInt(summaryData.PrevYTDProfileAccessedCount) 
        },
        mtd: { 
          current: safeParseInt(summaryData.CurrentMTDProfileAccessedCount), 
          previous: safeParseInt(summaryData.PrevMTDProfileAccessedCount) 
        }
      },
      kycDue: 0, // This field is not in the API response, so we'll keep it as 0 or remove it
      verification: {
        dvs: {
          submitted: safeParseInt(summaryData.DVS_Submitted),
          verified: safeParseInt(summaryData.DVS_Verified),
          retries: safeParseInt(summaryData.DVS_Retries)
        },
        aml: {
          requests: safeParseInt(summaryData.AML_Requests),
          approved: safeParseInt(summaryData.AML_Approved),
          rejected: safeParseInt(summaryData.AML_Rejected)
        }
      }
    };
  };

  const dashboardData = getDashboardData();

  const getPercentageChange = (current, previous) => {
    const change = ((current - previous) / previous) * 100;
    return change.toFixed(1);
  };

  const renderMetricCard = (
    title, 
    current, 
    previous, 
    Icon, 
    color = 'blue'
  ) => {
    const change = getPercentageChange(current, previous);
    const isPositive = parseFloat(change) > 0;
    
    return (
      <Card>
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className={`w-12 h-12 bg-${color}-100 rounded-lg flex items-center justify-center`}>
                <Icon className={`h-6 w-6 text-${color}-600`} />
              </div>
              <div className="ml-4">
                <CardDescription>{title}</CardDescription>
                <CardTitle className="text-2xl">{current.toLocaleString()}</CardTitle>
              </div>
            </div>
            <div className="text-right">
              <div className={`flex items-center ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                {isPositive ? (
                  <TrendingUp className="h-4 w-4 mr-1" />
                ) : (
                  <TrendingDown className="h-4 w-4 mr-1" />
                )}
                <span className="text-sm font-medium">{change}%</span>
              </div>
              <p className="text-xs text-muted-foreground mt-1">vs Previous Period</p>
            </div>
          </div>
        </CardHeader>
        <CardFooter className="pt-2 border-t">
          <p className="text-sm text-muted-foreground">
            Previous: <span className="font-medium">{previous.toLocaleString()}</span>
          </p>
        </CardFooter>
      </Card>
    );
  };

  const renderSimpleCard = (title, value, Icon, color = 'blue') => (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`w-10 h-10 bg-${color}-100 rounded-lg flex items-center justify-center`}>
              <Icon className={`h-5 w-5 text-${color}-600`} />
            </div>
            <div className="ml-3">
              <CardDescription>{title}</CardDescription>
              <CardTitle className="text-xl">{value.toLocaleString()}</CardTitle>
            </div>
          </div>
        </div>
      </CardHeader>
    </Card>
  )

  // Show loading state
  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-gray-600" />
            <p className="text-gray-600">Loading dashboard data...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state (but we fallback to mock data, so this rarely shows)
  if (error && !summaryData) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <AlertTriangle className="h-8 w-8 mx-auto mb-4 text-red-600" />
            <p className="text-red-600 mb-2">Failed to load dashboard data</p>
            <p className="text-gray-500 text-sm">{error}</p>
            <Button 
              variant="outline" 
              className="mt-4"
              onClick={() => window.location.reload()}
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* content begins directly under persistent title */}
      {/* removed demo/alert container when falling back to mock data */}

      {/* Profile Stats Overview */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Profile Statistics</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <Card className="flex flex-col min-h-[12rem]">
            <CardHeader className="flex-grow">
              <CardTitle>Total Profiles</CardTitle>
              <CardDescription>All registered profiles</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <p className="text-4xl font-bold">{dashboardData.profileStats.total.toLocaleString()}</p>
            </CardContent>
          </Card>
          
          <Card className="flex flex-col min-h-[12rem]">
            <CardHeader className="flex-grow">
              <CardTitle>High Confidence</CardTitle>
              <CardDescription>Profiles created, verified and accessed with 3 recent faceprints.</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <p className="text-4xl font-bold text-green-600">{dashboardData.profileStats.highConfidence.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">
                {((dashboardData.profileStats.highConfidence / dashboardData.profileStats.total) * 100).toFixed(1)}% of total
              </p>
            </CardContent>
          </Card>
          
          <Card className="flex flex-col min-h-[12rem]">
            <CardHeader className="flex-grow">
              <CardTitle>Medium Confidence</CardTitle>
              <CardDescription>Profiles created and verified by RXProfile</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <p className="text-4xl font-bold text-amber-600">{dashboardData.profileStats.mediumConfidence.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">
                {((dashboardData.profileStats.mediumConfidence / dashboardData.profileStats.total) * 100).toFixed(1)}% of total
              </p>
            </CardContent>
          </Card>
          
          <Card className="flex flex-col min-h-[12rem]">
            <CardHeader className="flex-grow">
              <CardTitle>Low Confidence</CardTitle>
              <CardDescription>Profiles created based on 3rd party system photo & data</CardDescription>
            </CardHeader>
            <CardContent className="mt-auto">
              <p className="text-4xl font-bold text-red-600">{dashboardData.profileStats.lowConfidence.toLocaleString()}</p>
              <p className="text-sm text-muted-foreground">
                {((dashboardData.profileStats.lowConfidence / dashboardData.profileStats.total) * 100).toFixed(1)}% of total
              </p>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Year to Date Performance */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Year to Date Performance</h3>
        <div className="grid grid-cols-2 gap-6">
          {renderMetricCard(
            'Profiles Created (YTD)',
            dashboardData.profilesCreated.ytd.current,
            dashboardData.profilesCreated.ytd.previous,
            Users,
            'blue'
          )}
          
          {renderMetricCard(
            'Profiles Accessed (YTD)',
            dashboardData.profilesAccessed.ytd.current,
            dashboardData.profilesAccessed.ytd.previous,
            BarChart3,
            'green'
          )}
        </div>
      </div>

      {/* Month to Date Performance */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Month to Date Performance</h3>
        <div className="grid grid-cols-2 gap-6">
          {renderMetricCard(
            'Profiles Created (MTD)',
            dashboardData.profilesCreated.mtd.current,
            dashboardData.profilesCreated.mtd.previous,
            Users,
            'purple'
          )}
          
          {renderMetricCard(
            'Profiles Accessed (MTD)',
            dashboardData.profilesAccessed.mtd.current,
            dashboardData.profilesAccessed.mtd.previous,
            BarChart3,
            'indigo'
          )}
        </div>
      </div>

      {/* Verification & Screening */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Verification & Screening</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-6">
          {renderSimpleCard('DVS Submitted', dashboardData.verification.dvs.submitted, FileInput, 'blue')}
          {renderSimpleCard('DVS Verified', dashboardData.verification.dvs.verified, FileCheck, 'green')}
          {renderSimpleCard('DVS Retries', dashboardData.verification.dvs.retries, RefreshCw, 'amber')}
          {renderSimpleCard('AML/PEP Requests', dashboardData.verification.aml.requests, ShieldCheck, 'blue')}
          {renderSimpleCard('AML/PEP Approved', dashboardData.verification.aml.approved, ShieldCheck, 'green')}
          {renderSimpleCard('AML/PEP Rejected', dashboardData.verification.aml.rejected, ShieldX, 'red')}
        </div>
      </div>

      {/* Daily Statistics */}
      <div className="mb-8">
        <h3 className="text-xl font-semibold text-gray-800 mb-4">Daily Statistics</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-blue-600" />
                Today's Profiles
              </CardTitle>
              <CardDescription>Profiles created today</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-blue-600">
                {summaryData ? safeParseInt(summaryData.CurrentDTDProfileCreatedCount).toLocaleString() : '0'}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <BarChart3 className="h-5 w-5 mr-2 text-green-600" />
                Yesterday's Profiles
              </CardTitle>
              <CardDescription>Profiles created yesterday</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-3xl font-bold text-green-600">
                {summaryData ? safeParseInt(summaryData.PrevDTDProfileCreatedCount).toLocaleString() : '0'}
              </p>
            </CardContent>
          </Card>
          
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center">
                <CheckCircle className="h-5 w-5 mr-2 text-purple-600" />
                API Status
              </CardTitle>
              <CardDescription>Last data refresh</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-lg font-bold text-green-600">✓ Connected</p>
              <p className="text-sm text-gray-500 mt-1">
                {summaryData ? `Version 1.0.0.1` : 'No data'}
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PatronDashboard